// NotificationsBell — self-contained header bell: unread badge polled
// every 60s, popover with the latest 15 notifications, mark-read on
// click (+ navigate when the row carries a link), mark-all-read.
//
// Mounted in components/Header/Header.jsx (desktop header row). Kept
// OUT of Header's inline HeaderComp on purpose: inline component types
// are re-created every render, which would unmount/remount this bell
// and reset its poll interval (see the App.jsx May-24 incident note).
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Badge, Popover, Button, Spin, Empty } from "antd";
import { BellOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  getNotifications,
  getUnreadCount,
  markRead,
  markAllRead,
} from "api/notifications/notificationsApi";

const POLL_MS = 60000;

export default function NotificationsBell() {
  const history = useHistory();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(() => {
    getUnreadCount()
      .then((res) => setCount(res?.count || 0))
      .catch(() => {}); // silent — badge is best-effort
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  const loadRows = useCallback(() => {
    setLoading(true);
    getNotifications({ limit: 15 })
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) loadRows();
  };

  const handleClick = (n) => {
    if (!n.readAt) {
      markRead(n._id).catch(() => {});
      setRows((prev) =>
        prev.map((r) =>
          r._id === n._id ? { ...r, readAt: new Date().toISOString() } : r
        )
      );
      setCount((c) => Math.max(0, c - 1));
    }
    if (n.link) {
      setOpen(false);
      history.push(n.link);
    }
  };

  const handleMarkAll = () => {
    markAllRead()
      .then(() => {
        setCount(0);
        setRows((prev) =>
          prev.map((r) =>
            r.readAt ? r : { ...r, readAt: new Date().toISOString() }
          )
        );
      })
      .catch(() => {});
  };

  const content = (
    <div style={{ width: 340, maxHeight: 420, overflowY: "auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 4,
        }}
      >
        <span style={{ fontWeight: 600 }}>Notifications</span>
        <Button size="small" type="link" onClick={handleMarkAll} disabled={count === 0}>
          Mark all read
        </Button>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 16 }}>
          <Spin size="small" />
        </div>
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications"
          style={{ margin: "12px 0" }}
        />
      ) : (
        rows.map((n) => (
          <div
            key={n._id}
            onClick={() => handleClick(n)}
            style={{
              padding: "8px 6px",
              borderBottom: "1px solid #f0f0f0",
              cursor: n.link ? "pointer" : "default",
              background: n.readAt ? "transparent" : "#F0F7FF",
              borderRadius: 4,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: n.readAt ? 400 : 600,
                lineHeight: 1.35,
              }}
            >
              {n.title}
            </div>
            {n.body ? (
              <div
                style={{
                  fontSize: 12,
                  color: "#666",
                  marginTop: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {n.body}
              </div>
            ) : null}
            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
              {moment(n.createdAt).fromNow()}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "pointer",
          padding: "0 12px",
        }}
      >
        <Badge count={count} size="small" overflowCount={99}>
          <BellOutlined style={{ fontSize: 20, color: "#333" }} />
        </Badge>
      </span>
    </Popover>
  );
}
